import { Can, TabCard, TabProps, UnitLog, UnitTable } from 'components';
import { PartitionOutlined, PlusOutlined, ExperimentOutlined, FunnelPlotOutlined, UsergroupAddOutlined, HistoryOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Paths, UnitType, User } from 'constants/index';
import { Button } from 'antd';
import { Drawers } from 'app/types/drawer';
import { useOpenDrawerHandler } from 'hooks';
import { useSelector } from 'react-redux';
import { selectors } from 'app/services/units';
import UsersTable from 'components/Unit/UsersTable';
import { useHasRoleInUnit } from 'hooks/authorization/useHasRole';

const Tabs = () => {
  const { t } = useTranslation();
  const unit = useSelector(selectors.getUnit);

  const handleAddRetailer = useOpenDrawerHandler({ key: Drawers.ADD_UNIT, props: { type: UnitType.RETAILER, path: Paths.ADMIN_RETAILERS_VIEW } });
  const handleAddLaboratory = useOpenDrawerHandler({ key: Drawers.ADD_UNIT, props: { type: UnitType.LABORATORY, path: Paths.ADMIN_LABORATORIES_VIEW } });
  const handleAddSampler = useOpenDrawerHandler({ key: Drawers.ADD_UNIT, props: { type: UnitType.SAMPLER, path: Paths.ADMIN_SAMPLERS_VIEW } });
  const handleAddUser = useOpenDrawerHandler({ key: Drawers.ADD_USER, props: { unitId: unit?.Id } });
  const showLogs = useHasRoleInUnit(User.Roles.ADMIN, unit?.Id);

  const addRetailerButton = (
    <Can requiredPermission={User.Permissions.MANAGE_UNIT}>
      <Button onClick={handleAddRetailer} icon={<PlusOutlined />} type='link'>
        {t('common.add', { type: t('entities.unit.retailer').toLowerCase() })}
      </Button>
    </Can>
  );

  const addLaboratoryButton = (
    <Can requiredPermission={User.Permissions.MANAGE_UNIT}>
      <Button onClick={handleAddLaboratory} icon={<PlusOutlined />} type='link'>
        {t('common.add', { type: t('entities.unit.laboratory').toLowerCase() })}
      </Button>
    </Can>
  );

  const addSamplerButton = (
    <Can requiredPermission={User.Permissions.MANAGE_UNIT}>
      <Button onClick={handleAddSampler} icon={<PlusOutlined />} type='link'>
        {t('common.add', { type: t('entities.unit.sampler').toLowerCase() })}
      </Button>
    </Can>
  );

  const addUsersButton = (
    <Can requiredPermission={User.Permissions.MANAGE_USERS}>
      <Button onClick={handleAddUser} icon={<PlusOutlined />} type='link'>
        {t('common.add', { type: t('entities.user.title') })}
      </Button>
    </Can>
  );

  const tabs: TabProps[] = [
    {
      key: 'retailers',
      title: t('tabs.retailers'),
      icon: <PartitionOutlined />,
      content: <UnitTable path={Paths.ADMIN_RETAILERS_VIEW} type={UnitType.RETAILER} nested />,
      actions: addRetailerButton
    },
    {
      key: 'laboratories',
      title: t('tabs.laboratories'),
      icon: <ExperimentOutlined />,
      content: <UnitTable path={Paths.ADMIN_LABORATORIES_VIEW} type={UnitType.LABORATORY} nested />,
      actions: addLaboratoryButton
    },
    {
      key: 'samplers',
      title: t('tabs.samplers'),
      icon: <FunnelPlotOutlined />,
      content: <UnitTable path={Paths.ADMIN_SAMPLERS_VIEW} type={UnitType.SAMPLER} nested />,
      actions: addSamplerButton
    },
    {
      key: 'users',
      title: t('tabs.users'),
      icon: <UsergroupAddOutlined />,
      content: <UsersTable unitId={unit?.Id} />,
      actions: addUsersButton
    },
  ];

  if (showLogs) tabs.push({
    key: 'log',
    title: t('tabs.log'),
    icon: <HistoryOutlined />,
    content: <UnitLog />
  });

  return <TabCard tabs={tabs} />;
};

export default Tabs;